/**
 * 👑 LEADERBOARD TAB - Architecture Mozart/Jobs/Tesla
 * 
 * L'onglet du classement.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Crown, Trophy, Medal, Star, TrendingUp } from 'lucide-react';
import { Button } from '../../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LeaderboardTab = ({ customerId, isDarkMode }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [period, setPeriod] = useState('all'); // all, week, month
  const [isLoading, setIsLoading] = useState(false);

  const loadLeaderboard = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/heartbeats/leaderboard?period=${period}`);
      const data = await response.json();
      if (data.success) {
        setLeaderboard(data.leaderboard || []);
        // Trouver mon rang
        const myEntry = data.leaderboard?.find(e => e.customerId === customerId);
        setMyRank(myEntry?.rank || null);
      }
    } catch (err) {
      console.error('Error loading leaderboard:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLeaderboard();
  }, [period]);

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600 to-amber-700 text-white';
    return isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="text-5xl mb-2">🏆</div>
        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Classement
        </h2>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Les plus populaires de Heartbeats
        </p>
      </div>

      {/* Période */}
      <div className="flex justify-center gap-2">
        {[
          { id: 'all', label: 'Tout' },
          { id: 'month', label: 'Ce mois' },
          { id: 'week', label: 'Cette semaine' }
        ].map(p => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              period === p.id
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                : isDarkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Mon rang */}
      {myRank && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-2xl text-center ${
            isDarkMode 
              ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-700/30' 
              : 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200'
          }`}
        >
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Ton classement</p>
          <p className={`text-3xl font-bold mt-1 ${
            isDarkMode ? 'text-purple-300' : 'text-purple-600'
          }`}>
            #{myRank}
          </p>
        </motion.div>
      )}

      {/* Liste */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin text-4xl">🏆</div>
        </div>
      ) : leaderboard.length === 0 ? (
        <div className="text-center py-8">
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
            Pas encore de classement
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {leaderboard.map((entry, index) => (
            <motion.div
              key={entry.customerId}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`p-4 rounded-2xl flex items-center gap-4 ${
                entry.customerId === customerId
                  ? 'ring-2 ring-purple-500'
                  : ''
              } ${
                isDarkMode ? 'bg-gray-800/70' : 'bg-white shadow-sm'
              }`}
            >
              {/* Rang */}
              <div className={`w-14 h-14 rounded-full flex items-center justify-center font-bold text-lg ${getRankStyle(entry.rank)}`}>
                {getRankIcon(entry.rank)}
              </div>

              {/* Avatar */}
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-xl">
                {entry.avatar || '😊'}
              </div>

              {/* Info */}
              <div className="flex-1">
                <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {entry.displayName}
                  {entry.customerId === customerId && ' (toi)'}
                </h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {entry.city || 'France'}
                </p>
              </div>

              {/* Score */}
              <div className="text-right">
                <div className="flex items-center gap-1">
                  <Crown className="w-4 h-4 text-yellow-500" />
                  <span className={`font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                    {entry.crowns}
                  </span>
                </div>
                {entry.change && (
                  <p className={`text-xs flex items-center gap-1 ${
                    entry.change > 0 ? 'text-green-500' : entry.change < 0 ? 'text-red-500' : 'text-gray-400'
                  }`}>
                    <TrendingUp className={`w-3 h-3 ${
                      entry.change < 0 ? 'rotate-180' : ''
                    }`} />
                    {entry.change > 0 ? '+' : ''}{entry.change}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeaderboardTab;
